// frontend/src/pages/ProviderSetup.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  User, 
  Building, 
  CheckCircle, 
  AlertCircle,
  Clock
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { tokenManager } from '@/lib/httpClient';
import EmailVerificationNotification from '@/components/common/EmailVerificationNotification';
import BusinessProfileForm from '@/components/Provider/BusinessProfileForm';
import ProviderSlugDisplay from '@/components/Provider/ProviderSlugDisplay';
import ProviderSlugSetupForm from '@/components/Provider/ProviderSlugSetupForm';
import ProviderQuickActions from '@/components/Provider/ProviderQuickActions';

interface BusinessProfile {
  business_name: string;
  description: string;
  years_experience: number;
  business_type: string;
  business_license: string;
  insurance_details: string;
  website: string;
  business_phone: string;
  business_email: string;
  about_me: string;
  specializations: string[];
  certifications: string[];
  provider_slug_url?: string;
}

interface ProviderData {
  provider_slug_url?: string;
  business_name?: string;
}

const ProviderSetup: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [providerData, setProviderData] = useState<ProviderData>({});
  const [selectedSlug, setSelectedSlug] = useState<string>('');
  const [profileData, setProfileData] = useState<BusinessProfile>({
    business_name: '',
    description: '',
    years_experience: 1,
    business_type: 'individual',
    business_license: '',
    insurance_details: '',
    website: '',
    business_phone: '',
    business_email: '',
    about_me: '',
    specializations: [],
    certifications: [],
    // Additional fields
    address: '',
    city: '',
    provider_slug_url: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    preferred_language: 'en'
  });

  // Get any messages from navigation state
  const stateMessage = location.state?.message;
  const messageType = location.state?.type || 'info';

  useEffect(() => {
    // Redirect if not a provider
    if (user && user.role !== 'provider') {
      navigate('/dashboard');
    }
    
    // Load existing profile data
    loadProfileData();
  }, [user, navigate]);

  const loadProfileData = async () => {
    try {
      const token = tokenManager.getToken();
      if (!token) return;

      const response = await fetch('/api/provider/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.provider) {
          const profile = data.data.provider;
          
          // Set provider data (including slug URL)
          setProviderData({
            provider_slug_url: profile.provider_slug_url,
            business_name: profile.business_name,
          });

          // Set profile data
          setProfileData({
            business_name: profile.business_name || '',
            description: profile.description || '',
            years_experience: profile.years_experience || 1,
            business_type: profile.business_type || 'individual',
            business_license: profile.business_license || '',
            insurance_details: profile.insurance_details || '',
            website: profile.website || '',
            business_phone: profile.business_phone || profile.phone_number || user?.phone || '',
            business_email: profile.business_email || user?.email || '',
            about_me: profile.about_me || '',
            specializations: profile.specializations ? 
              (typeof profile.specializations === 'string' ? 
                JSON.parse(profile.specializations) : profile.specializations) : [],
            certifications: profile.certifications ? 
              (typeof profile.certifications === 'string' ? 
                JSON.parse(profile.certifications) : profile.certifications) : [],
            // Additional fields
            address: profile.address || '',
            city: profile.city || '',
            provider_slug_url: profile.provider_slug_url || '',
            first_name: profile.first_name || user?.first_name || '',
            last_name: profile.last_name || user?.last_name || '',
            email: profile.email || user?.email || '',
            phone_number: profile.phone_number || user?.phone || '',
            preferred_language: profile.preferred_language || 'en'
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!profileData.business_name.trim()) {
      newErrors.business_name = 'Business name is required';
    }
    if (!profileData.description.trim()) {
      newErrors.description = 'Business description is required';
    }
    if (profileData.years_experience < 1) {
      newErrors.years_experience = 'Years of experience must be at least 1';
    }
    if (!profileData.about_me.trim()) {
      newErrors.about_me = 'About me section is required';
    }
    if (profileData.business_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.business_email)) {
      newErrors.business_email = 'Valid email address is required';
    }
    if (profileData.website && !/^https?:\/\/.+/.test(profileData.website)) {
      newErrors.website = 'Valid website URL is required (include http:// or https://)';
    }

    // Check if slug is set when creating new profile
    if (!providerData.provider_slug_url && !selectedSlug) {
      newErrors.provider_slug_url = 'Please set up your profile URL first';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDataChange = (data: BusinessProfile) => {
    setProfileData(data);
    setErrors({}); // Clear errors when data changes
  };

  const handleSlugSet = (slug: string) => {
    setSelectedSlug(slug);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.provider_slug_url;
      return newErrors;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const token = tokenManager.getToken();
      
      // Include the selected slug URL in the profile data
      const submitData = {
        ...profileData,
        provider_slug_url: selectedSlug || profileData.provider_slug_url
      };

      const response = await fetch('/api/provider/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        
        // Update provider data with new information
        if (data.data && data.data.provider) {
          setProviderData({
            provider_slug_url: data.data.provider.provider_slug_url,
            business_name: data.data.provider.business_name,
          });
        }

        setTimeout(() => {
          navigate('/dashboard', {
            state: {
              message: 'Business profile saved successfully! You can now set up your services.',
              type: 'success'
            }
          });
        }, 2000);
      } else {
        if (data.data && data.data.errors) {
          setErrors(data.data.errors);
        } else {
          setErrors({ general: data.message || 'Profile save failed. Please try again.' });
        }
      }
    } catch (error: any) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Email Verification Notification */}
        <EmailVerificationNotification />

        {/* State Message */}
        {stateMessage && (
          <div className={`mb-6 border rounded-lg p-4 ${
            messageType === 'success' ? 'bg-green-50 border-green-200' :
            messageType === 'error' ? 'bg-red-50 border-red-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {messageType === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : messageType === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-blue-400" />
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  messageType === 'success' ? 'text-green-800' :
                  messageType === 'error' ? 'text-red-800' :
                  'text-blue-800'
                }`}>
                  {stateMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-blue-500 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Provider Setup</h1>
              <p className="text-gray-600 mt-1">
                Complete your business profile to build trust with customers and improve your visibility.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <ProviderQuickActions />

        {/* Provider Slug URL Section */}
        {providerData.provider_slug_url ? (
          // Show existing slug (read-only)
          <ProviderSlugDisplay 
            slugUrl={providerData.provider_slug_url}
            businessName={providerData.business_name}
            className="mb-8"
          />
        ) : (
          // Show slug setup form for new providers
          <ProviderSlugSetupForm
            businessName={profileData.business_name}
            onSlugSet={handleSlugSet}
            className="mb-8"
          />
        )}

        {/* Error for missing slug URL */}
        {errors.provider_slug_url && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {errors.provider_slug_url}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Business Profile Form */}
        <BusinessProfileForm
          profileData={profileData}
          onDataChange={handleDataChange}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          errors={errors}
          success={success}
        />

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Next Steps</h3>
          <div className="space-y-3">
            <div className="flex items-center text-blue-800">
              <Clock className="h-5 w-5 mr-2" />
              <span>Set up your profile URL {providerData.provider_slug_url ? '✓' : '(current step)'}</span>
            </div>
            <div className="flex items-center text-blue-800">
              <Clock className="h-5 w-5 mr-2" />
              <span>Complete your business profile {success ? '✓' : '(current step)'}</span>
            </div>
            <div className="flex items-center text-blue-700">
              <Clock className="h-5 w-5 mr-2" />
              <span>Set up your services and pricing</span>
            </div>
            <div className="flex items-center text-blue-700">
              <Clock className="h-5 w-5 mr-2" />
              <span>Configure your service areas</span>
            </div>
            <div className="flex items-center text-blue-700">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>Start receiving bookings!</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderSetup;